import classNames from 'classnames';
import { useEffect, useState } from 'react';
import type { TooltipRenderProps } from 'react-joyride';

import { useBrand } from '../../shared/branding/brand-provider';
import { getTourStepData } from './tour.config';
import { isElementVisible, pollUntil } from './tour.utils';
import { INTRO_LEDE } from './tour-copy';
import styles from './tour-tooltip.module.css';

/**
 * Blocks Next until every selector in `waitForTargets` is in the DOM and
 * visible. Used on the step before the filter panel, whose targets only mount
 * once the page's data has resolved — without it, Next lands on a step whose
 * target doesn't exist yet and Joyride falls back to a centered tooltip.
 *
 * Resolves to `true` on timeout as well: a blocked Next is worse than an
 * imperfectly-placed one.
 */
function useTargetsReady(selectors: readonly string[] | undefined): boolean {
  const [ready, setReady] = useState(() => !selectors?.length);

  useEffect(() => {
    if (!selectors?.length) {
      setReady(true);
      return;
    }

    setReady(false);
    return pollUntil(
      () =>
        selectors.every((selector) => {
          const element = document.querySelector(selector);
          return element instanceof HTMLElement && isElementVisible(element);
        }),
      () => setReady(true),
      () => setReady(true)
    );
  }, [selectors]);

  return ready;
}

function ProgressRail({ index, size }: { index: number; size: number }) {
  return (
    <div className={styles.rail} aria-hidden="true">
      {Array.from({ length: size }, (_, segment) => (
        <span
          key={segment}
          className={classNames(
            styles.segment,
            segment <= index && styles.segmentDone
          )}
        />
      ))}
    </div>
  );
}

/**
 * Opening card: a welcome headline and the site's lede. The headline is
 * brand-aware, so a whitelabel client sees their own name rather than Revelio's.
 */
function IntroCard() {
  const { displayName } = useBrand();

  return (
    <>
      <h4 className={styles.title}>Welcome to {displayName}</h4>
      <div className={styles.body}>{INTRO_LEDE}</div>
    </>
  );
}

export function TourTooltip({
  index,
  size,
  step,
  backProps,
  skipProps,
  primaryProps,
  tooltipProps,
}: TooltipRenderProps) {
  const data = getTourStepData(step);
  const isIntro = data?.isIntro === true;
  const isReady = useTargetsReady(data?.waitForTargets);
  const isLastStep = index === size - 1;

  // Joyride's primaryProps carry aria-label="Next" from its locale. Our label
  // changes at the ends of the tour, so name the button after what it reads —
  // otherwise a screen reader announces "Next" on a button saying "Finish".
  // biome-ignore lint/style/noNestedTernary: three-way label, flattening reads worse
  const primaryLabel = isIntro ? 'Start tour' : isLastStep ? 'Finish' : 'Next';

  return (
    <div
      {...tooltipProps}
      className={classNames(styles.tooltip, isIntro && styles.intro)}
      data-testid="tour-tooltip"
      data-tour-step={data?.phase}
    >
      {isIntro ? (
        <IntroCard />
      ) : (
        <>
          {data?.kicker && <span className={styles.kicker}>{data.kicker}</span>}
          <h4 className={styles.title} data-testid="tour-step-title">
            {step.title}
          </h4>
          <div className={styles.body} data-testid="tour-step-body">
            {step.content}
          </div>
        </>
      )}

      <ProgressRail index={index} size={size} />

      <div className={styles.footer}>
        {/* Accessible name is "Skip Tour" (not the visible "Skip") to match the
            legacy tour and the shared `dismissOverlayDialogsIfPresent` helper,
            which dismisses the tour via `getByRole('button', {name:'Skip Tour'})`
            across the suite — and it reads clearer to a screen reader. */}
        <button
          {...skipProps}
          type="button"
          className={styles.ghost}
          aria-label="Skip Tour"
        >
          Skip
        </button>
        <span className={styles.spacer} />
        {index > 0 && (
          <button {...backProps} type="button" className={styles.ghost}>
            Back
          </button>
        )}
        <button
          {...primaryProps}
          type="button"
          className={styles.next}
          disabled={!isReady}
          aria-label={primaryLabel}
        >
          {primaryLabel}
        </button>
      </div>
    </div>
  );
}
